import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

/**
 * VIGNELLI BADGE SYSTEM
 * 
 * Core semantic variants:
 * - default: Primary/neutral actions
 * - success: Completed, active, positive states  
 * - warning: Pending, attention needed
 * - destructive: Error, blocked, critical
 * - outline: Subtle emphasis
 * - secondary: Alias for default (backwards compatibility)
 */
const badgeVariants = cva(
  "inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold transition-colors",
  {
    variants: {
      variant: {
        // Default - neutral, informational
        default: "border-transparent bg-muted text-foreground",
        
        // Secondary - same as default (backward compatibility)
        secondary: "border-transparent bg-muted text-muted-foreground",
        
        // Semantic triad
        success: "border-success/20 bg-success/10 text-success",
        warning: "border-warning/20 bg-warning/10 text-warning", 
        destructive: "border-destructive/20 bg-destructive/10 text-destructive",
        
        // Informational (maps to default styling)
        info: "border-transparent bg-muted text-foreground",
        
        // Workflow states (mapped to semantic triad)
        pending: "border-warning/20 bg-warning/10 text-warning",
        inProgress: "border-warning/20 bg-warning/10 text-warning",
        completed: "border-success/20 bg-success/10 text-success",
        cancelled: "border-transparent bg-muted text-muted-foreground",
        active: "border-success/20 bg-success/10 text-success",
        inactive: "border-transparent bg-muted text-muted-foreground",
        suspended: "border-destructive/20 bg-destructive/10 text-destructive",
        draft: "border-transparent bg-muted text-muted-foreground",
        archived: "border-transparent bg-muted text-muted-foreground",
        error: "border-destructive/20 bg-destructive/10 text-destructive",
        
        // Outline for subtle emphasis
        outline: "border-border bg-transparent text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }

/**
 * Helper to map legacy status strings to new badge variants
 */
export function getStatusVariant(status: string): "default" | "success" | "warning" | "destructive" | "outline" {
  const successStates = ['active', 'completed', 'success', 'approved'];
  const warningStates = ['pending', 'in-progress', 'stalled', 'review', 'draft', 'scheduled'];
  const destructiveStates = ['error', 'failed', 'suspended', 'cancelled', 'blocked', 'critical'];
  
  if (successStates.includes(status)) return 'success';
  if (warningStates.includes(status)) return 'warning';
  if (destructiveStates.includes(status)) return 'destructive';
  return 'default';
}